import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import PublicLayout from "@/components/PublicLayout";
import { useSEO, breadcrumbLd, webPageLd } from "@/components/Seo";
import { PRODUCTS } from "@/config/site";

const CTA = () => (
  <div className="flex flex-wrap gap-3 mt-8">
    <Link to="/register" className="inline-flex items-center gap-1.5 rounded-lg bg-vs-royal px-5 py-2.5 text-sm font-semibold text-white hover:bg-vs-blue transition-colors" data-testid="seo-cta-register">
      Become a Partner<ArrowRight className="h-4 w-4" /></Link>
    <Link to="/contact" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:border-vs-royal hover:text-vs-royal transition-colors" data-testid="seo-cta-contact">
      Contact VS-Solutions</Link>
  </div>
);

function SEOPage({ cfg, children }) {
  useSEO({
    title: cfg.title, description: cfg.description, path: cfg.path,
    jsonLd: { "@context": "https://schema.org", "@graph": [webPageLd(cfg), breadcrumbLd(cfg.trail)] },
  });
  return (
    <PublicLayout>
      <article>
        <section className="bg-gradient-to-b from-vs-light/60 to-white border-b border-slate-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 max-w-3xl">
            <div className="text-xs uppercase tracking-wider font-semibold text-vs-royal">{cfg.eyebrow}</div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl text-vs-ink mt-3 tracking-tight">{cfg.h1}</h1>
            <p className="text-slate-600 text-base sm:text-lg mt-5 leading-relaxed">{cfg.intro}</p>
            <CTA />
          </div>
        </section>
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
          <div className="grid md:grid-cols-2 gap-6">
            {cfg.sections.map((s) => (
              <div key={s.h} className="vs-card p-6 sm:p-7">
                <h2 className="font-display font-bold text-lg text-vs-ink">{s.h}</h2>
                <p className="text-sm text-slate-600 mt-2.5 leading-relaxed">{s.p}</p>
                {s.bullets && (
                  <ul className="mt-4 space-y-2">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-slate-600">
                        <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />{b}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
          {children}
        </section>
      </article>
    </PublicLayout>
  );
}

/* ---------------------------------- Platform ---------------------------------- */
export function PlatformPage() {
  return (
    <SEOPage cfg={{
      path: "/platform",
      title: "VS PartnerHub Platform | Enterprise Partner Management by Vijay Software Solutions",
      description: "Explore the VS PartnerHub platform: client, vendor and staffing partner workspaces, procurement and staffing workflows, agreements, NDA/SLA management and secure partner operations — by Vijay Software Solutions Pvt Ltd.",
      eyebrow: "The Platform",
      h1: "VS PartnerHub — Enterprise Partner Management Platform",
      trail: [["Home", "/"], ["Platform", "/platform"]],
      intro: "VS PartnerHub is the enterprise partner management platform built and operated by Vijay Software Solutions Pvt Ltd (VS-Solutions). It connects clients, vendors and staffing companies in one secure environment for requirements, procurement, staffing, agreements and day-to-day business collaboration.",
      sections: [
        { h: "Three Connected Workspaces", p: "Every partner operates inside a purpose-built workspace with role-based access and organization-level data isolation.", bullets: ["Client Workspace — requirements, RFQs, procurement and hiring oversight", "Vendor Workspace — RFQ invitations, quotations, orders, contracts and finance", "Staffing Partner Workspace — requirements, candidates, submissions, interviews and deployments"] },
        { h: "Procurement Workflows", p: "A complete procure-to-pay cycle managed natively on the platform.", bullets: ["RFQs / RFPs issued to verified vendors", "Quotation submission and comparison", "Purchase orders, contracts, invoices and payments"] },
        { h: "Staffing Workflows", p: "End-to-end staffing operations from requirement to deployment.", bullets: ["Requirement creation and assignment to staffing partners", "Candidate submissions, interviews and selections", "Offers, background verification coordination and joining"] },
        { h: "Agreements, NDA & SLA Management", p: "A structured agreement engine generates, versions and manages NDAs, SLAs and master agreements with clause libraries, party auto-population, document validation and a full audit trail — all inside the platform." },
        { h: "Partner Onboarding & Verification", p: "Structured onboarding with configurable profile and document requirements per partner type, verification workflows and approval gates managed by the VS-Solutions team." },
        { h: "Secure Partner Operations", p: "Authentication-protected workspaces, organization-scoped data access, document management, notifications, support tickets and complete activity audit trails. Private business data is never exposed publicly." },
      ],
    }} />
  );
}

/* ---------------------------------- Solutions ---------------------------------- */
export function SolutionsPage() {
  return (
    <SEOPage cfg={{
      path: "/solutions",
      title: "Solutions | Procurement, Staffing & Agreement Management — VS PartnerHub",
      description: "VS PartnerHub solutions: procurement workflows, staffing solutions, agreement and NDA/SLA management, partner onboarding and 360° record visibility for clients, vendors and staffing companies.",
      eyebrow: "Solutions",
      h1: "Procurement, Staffing and Agreement Management — Unified",
      trail: [["Home", "/"], ["Solutions", "/solutions"]],
      intro: "VS PartnerHub unifies the workflows that usually live across email threads, spreadsheets and disconnected tools — so Vijay Software Solutions and its partners work from one authoritative record.",
      sections: [
        { h: "Procurement", p: "From sourcing to payment, procurement runs as a connected workflow with full visibility at every stage.", bullets: ["RFQ creation and vendor invitations", "Quotation comparison and award", "Purchase orders and contract linkage", "Invoice submission and payment tracking"] },
        { h: "Staffing Solutions", p: "A complete staffing operations pipeline connecting clients and staffing partners.", bullets: ["Requirement publishing and partner assignment", "Candidate submission and review", "Interview scheduling and structured feedback", "Selection, offer coordination and deployment"] },
        { h: "Agreement & NDA/SLA Management", p: "Enterprise agreements generated from governed templates with a clause library, partner-type-aware content, version history and a controlled review-to-execution lifecycle.", bullets: ["NDA, SLA, MSA and partner agreements", "Party A / Party B auto-population from verified records", "Versioning, validation and audit trail"] },
        { h: "Partner Onboarding", p: "Dynamic, partner-type-specific onboarding with profile completion, document requirements and a verification process that produces genuine, auditable partner records." },
        { h: "360° Record Visibility", p: "Every requirement, quotation, candidate, contract, invoice and agreement carries its full lifecycle — status history, related records and activity — in one place." },
        { h: "Business Collaboration", p: "Notifications, support tickets, calendars and documents keep every authorized stakeholder aligned without leaving the platform." },
      ],
    }} />
  );
}

/* ---------------------------------- Clients ---------------------------------- */
export function ClientsPage() {
  return (
    <SEOPage cfg={{
      path: "/clients",
      title: "Client Workspace | VS PartnerHub — Vijay Software Solutions Partner Portal",
      description: "The VS PartnerHub Client Workspace: post requirements, run RFQs, compare quotations, manage purchase orders, contracts, invoices and review candidate submissions — in one secure client partner portal by Vijay Software Solutions Pvt Ltd.",
      eyebrow: "For Clients",
      h1: "Client Workspace — Your Partner Portal for Requirements, Procurement and Hiring",
      trail: [["Home", "/"], ["For Clients", "/clients"]],
      intro: "The Client Workspace on VS PartnerHub gives client organizations a single, secure portal to run requirements, procurement and staffing operations with Vijay Software Solutions Pvt Ltd and its verified partner network.",
      sections: [
        { h: "Requirements Management", p: "Create structured business and hiring requirements, track their status and monitor fulfilment end to end.", bullets: ["Guided requirement builder", "Status tracking from draft to closure", "Full requirement 360° history"] },
        { h: "Procurement Control", p: "Run sourcing cycles with complete visibility.", bullets: ["Issue RFQs and receive quotations", "Compare quotations side by side", "Purchase orders, contracts, invoices and payments"] },
        { h: "Staffing & Candidate Review", p: "Review candidate submissions from staffing partners, coordinate interviews and record selection decisions — all timestamped and auditable." },
        { h: "Agreements & Documents", p: "Access your NDAs, SLAs and agreements with Vijay Software Solutions, manage company documents and stay ahead of expiries from your workspace." },
      ],
    }} />
  );
}

/* ---------------------------------- Vendors ---------------------------------- */
export function VendorsPage() {
  return (
    <SEOPage cfg={{
      path: "/vendors",
      title: "Vendor Workspace | Vendor Portal — Vijay Software Solutions | VS PartnerHub",
      description: "The VS PartnerHub Vendor Workspace: receive RFQ invitations from Vijay Software Solutions, submit quotations, manage purchase orders, contracts, invoices and payments — the official vendor portal of Vijay Software Solutions Pvt Ltd.",
      eyebrow: "For Vendors",
      h1: "Vendor Workspace — The Official Vendor Portal of Vijay Software Solutions",
      trail: [["Home", "/"], ["For Vendors", "/vendors"]],
      intro: "Vendor companies partner with Vijay Software Solutions Pvt Ltd through the VS PartnerHub Vendor Workspace — a secure portal covering the complete RFQ-to-payment cycle with transparent status at every step.",
      sections: [
        { h: "RFQ Invitations & Quotations", p: "Receive RFQ invitations, respond with structured quotations and track evaluation outcomes without email chains.", bullets: ["RFQ inbox with deadlines", "Structured quotation submission", "Quotation status visibility"] },
        { h: "Orders, Contracts & Finance", p: "Manage the commercial relationship in one place.", bullets: ["Purchase order acknowledgement", "Contracts and agreements access", "Invoice submission and payment tracking"] },
        { h: "Onboarding & Verification", p: "A guided onboarding flow with clear document requirements and a transparent verification status — no ambiguity about what is needed or where you stand." },
        { h: "Performance & Compliance", p: "Business performance analytics, document expiry tracking and SLA-aligned operations help vendor partners stay compliant and responsive." },
      ],
    }} />
  );
}

/* ---------------------------------- Staffing ---------------------------------- */
export function StaffingPartnersPage() {
  return (
    <SEOPage cfg={{
      path: "/staffing-partners",
      title: "Staffing Partner Workspace | Staffing Partner Portal — Vijay Software Solutions",
      description: "The VS PartnerHub Staffing Partner Workspace: receive requirements, submit candidates, coordinate interviews, track selections and manage contracts and invoices — the staffing partner portal of Vijay Software Solutions Pvt Ltd.",
      eyebrow: "For Staffing Partners",
      h1: "Staffing Partner Workspace — Requirement to Deployment, One Pipeline",
      trail: [["Home", "/"], ["For Staffing Partners", "/staffing-partners"]],
      intro: "Staffing companies work with Vijay Software Solutions Pvt Ltd through the VS PartnerHub Staffing Partner Workspace — a purpose-built command center covering the full recruitment delivery pipeline.",
      sections: [
        { h: "Assigned Requirements", p: "Receive client requirements directly in your workspace with complete role details, timelines and status tracking." },
        { h: "Candidate Pipeline", p: "Manage your candidate database and submissions with structured profiles and real-time status.", bullets: ["Candidate submission against requirements", "Client review visibility", "Interview scheduling and feedback"] },
        { h: "Selections to Deployment", p: "Track selections, offer coordination, background verification and joining — every stage recorded on the platform." },
        { h: "Contracts, Invoices & Agreements", p: "Access your NDAs, SLAs and staffing agreements, submit invoices and track payments — with complete audit history." },
      ],
    }} />
  );
}

/* ---------------------------------- About ---------------------------------- */
export function AboutPage() {
  return (
    <SEOPage cfg={{
      path: "/about",
      title: "About Vijay Software Solutions Pvt Ltd | VS-Solutions — VS PartnerHub",
      description: "Vijay Software Solutions Pvt Ltd (VS-Solutions) is a technology company building enterprise platforms and consumer products. VS PartnerHub is its enterprise partner management platform at vs-partnerhub.com.",
      eyebrow: "About",
      h1: "About Vijay Software Solutions Pvt Ltd",
      trail: [["Home", "/"], ["About", "/about"]],
      intro: "Vijay Software Solutions Pvt Ltd — known by the brand VS-Solutions — is a technology company that builds enterprise platforms and digital products. VS PartnerHub is the company's enterprise partner management platform, operated at vs-partnerhub.com.",
      sections: [
        { h: "The Company", p: "Vijay Software Solutions Pvt Ltd builds software platforms spanning enterprise operations and consumer technology. The company operates with a product-first mindset: real workflows, real data integrity and security by design.", bullets: ["Enterprise platforms for partner management and business operations", "Consumer products across food technology, matrimony and safety", "Official company website: vs-solutions.in"] },
        { h: "Brand Architecture", p: "Vijay Software Solutions Pvt Ltd is the registered company. VS-Solutions is the brand. VS PartnerHub is the enterprise platform — the partner portal for clients, vendors and staffing companies working with the company." },
        { h: "VS PartnerHub", p: "VS PartnerHub (vs-partnerhub.com) is where the company's partner ecosystem operates: onboarding, procurement, staffing, agreements, documents and secure collaboration." },
      ],
    }}>
      <div className="mt-10 rounded-2xl bg-vs-light/60 border border-slate-100 p-6 sm:p-8">
        <h2 className="font-display font-bold text-lg text-vs-ink">Products by VS-Solutions</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
          {PRODUCTS.map((p) => (
            <div key={p.name} className="rounded-xl bg-white border border-slate-100 p-4">
              <div className={`h-9 w-9 rounded-lg bg-gradient-to-br ${p.gradient} flex items-center justify-center text-white font-display font-bold`}>{p.letter}</div>
              <div className="font-semibold text-sm text-slate-800 mt-3">{p.name}</div>
              <div className="text-xs text-slate-500 mt-1">{p.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </SEOPage>
  );
}

/* ---------------------------------- Products ---------------------------------- */
export function ProductsPage() {
  return (
    <SEOPage cfg={{
      path: "/products",
      title: "Products | VS-Solutions — VS PartnerHub, ManaJodi, BiteGo, VSSafeHer",
      description: "Products by Vijay Software Solutions Pvt Ltd (VS-Solutions): VS PartnerHub enterprise partner management platform, ManaJodi, BiteGo and VSSafeHer.",
      eyebrow: "Products",
      h1: "Products by VS-Solutions",
      trail: [["Home", "/"], ["Products", "/products"]],
      intro: "Vijay Software Solutions Pvt Ltd builds and operates a focused portfolio of platforms. VS PartnerHub is the enterprise partner management platform; the company's consumer products span food technology, matrimony and personal safety.",
      sections: PRODUCTS.map((p) => ({ h: `${p.name} — ${p.category}`, p: p.desc + (p.comingSoon ? " (Coming soon.)" : "") })),
    }} />
  );
}

/* ---------------------------------- Resources ---------------------------------- */
export function ResourcesPage() {
  const LINKS = [
    ["Platform Overview", "/platform", "What VS PartnerHub is and the modules it unifies."],
    ["Solutions", "/solutions", "Procurement, staffing, agreements and onboarding solutions."],
    ["Client Workspace", "/clients", "The client partner portal for requirements and procurement."],
    ["Vendor Workspace", "/vendors", "The vendor portal: RFQs, quotations, orders and finance."],
    ["Staffing Partner Workspace", "/staffing-partners", "The staffing command center: requirement to deployment."],
    ["Partner Onboarding", "/register", "Register your organization and complete guided onboarding."],
    ["About VS-Solutions", "/about", "The company behind VS PartnerHub."],
    ["Contact", "/contact", "Support and business enquiries."],
  ];
  return (
    <SEOPage cfg={{
      path: "/resources",
      title: "Resources | VS PartnerHub — Guides, Workspaces & Partner Onboarding",
      description: "VS PartnerHub resources: platform overview, solutions, client, vendor and staffing partner workspaces, partner onboarding and contact — by Vijay Software Solutions Pvt Ltd.",
      eyebrow: "Resources",
      h1: "VS PartnerHub Resources",
      trail: [["Home", "/"], ["Resources", "/resources"]],
      intro: "Everything you need to understand VS PartnerHub and get started as a client, vendor or staffing partner of Vijay Software Solutions Pvt Ltd.",
      sections: [],
    }}>
      <div className="grid sm:grid-cols-2 gap-4 -mt-6">
        {LINKS.map(([label, to, d]) => (
          <Link key={to} to={to} className="vs-card p-5 flex items-start justify-between gap-3 hover:border-vs-royal transition-colors group" data-testid={`resource-${to.slice(1) || "home"}`}>
            <div><div className="font-semibold text-slate-800 group-hover:text-vs-royal">{label}</div>
              <div className="text-sm text-slate-500 mt-1">{d}</div></div>
            <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-vs-royal shrink-0 mt-1" />
          </Link>
        ))}
      </div>
    </SEOPage>
  );
}
